/**
 * The one date module of the monorepo. Every app formats, parses and computes
 * dates through these helpers; nothing else calls date-fns or Intl directly.
 *
 * Conventions:
 * - An instant is a `Date` in memory, a `DateTime` column (UTC) in the database,
 *   and a UTC ISO 8601 string (`2026-10-25T14:28:53.991Z`) in JSON and on the wire.
 * - A calendar date (birth date, document date) is a `YYYY-MM-DD` string. When it
 *   has to live in a `DateTime` column it is stored at UTC midnight of that day.
 * - Business days, and everything shown to people, use the Paris time zone,
 *   whatever the time zone of the server, browser or phone.
 */
import { TZDate, tz } from "@date-fns/tz";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  formatDistance,
  isSameDay,
  isValid,
  startOfDay,
  startOfMonth,
  startOfYear,
} from "date-fns";
import { enGB, fr } from "date-fns/locale";

export const APP_TIME_ZONE = "Europe/Paris";

export type DateInput = Date | string | number;
export type DateLocale = "fr" | "en";
/** `YYYY-MM-DD`. */
export type CalendarDate = string;

const inAppZone = tz(APP_TIME_ZONE);
const inUtc = tz("UTC");
const locales = { fr, en: enGB } as const;
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** Display patterns; the locale only changes day and month names. */
export const DATE_PATTERNS = {
  date: "dd/MM/yyyy",
  dateTime: "dd/MM/yyyy HH:mm",
  time: "HH:mm",
  longDate: "EEEE d MMMM yyyy",
  longDateTime: { fr: "EEEE d MMMM yyyy 'à' HH:mm", en: "EEEE d MMMM yyyy 'at' HH:mm" },
} as const;

export interface FormatDateOptions {
  locale?: DateLocale;
  /** Returned for null, undefined and invalid dates. */
  fallback?: string;
}

// ---------------------------------------------------------------------------
// Parsing and serialization

/** Parses a Date, an ISO 8601 string or epoch milliseconds; invalid input gives null. */
export function toDate(value: DateInput | null | undefined): Date | null {
  if (value == null || value === "") return null;
  const date = new Date(value instanceof Date ? value.getTime() : value);
  return isValid(date) ? date : null;
}

/** UTC ISO 8601 string, the only format dates are written in to JSON. */
export function toIsoDateTime(value: DateInput): string {
  return requireDate(value).toISOString();
}

// ---------------------------------------------------------------------------
// Calendar dates (YYYY-MM-DD)

export function isCalendarDate(value: unknown): value is CalendarDate {
  if (typeof value !== "string") return false;
  const match = CALENDAR_DATE.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/**
 * The Paris calendar day of an instant. Reading in Paris also gives the right day for
 * values stored at UTC midnight and for older values stored at Paris midnight.
 */
export function toCalendarDate(value: DateInput): CalendarDate {
  if (isCalendarDate(value)) return value;
  return format(requireDate(value), "yyyy-MM-dd", { in: inAppZone });
}

/** Instant a calendar date is stored at in a `DateTime` column: UTC midnight. */
export function fromCalendarDate(date: CalendarDate): Date {
  if (!isCalendarDate(date)) throw new RangeError(`Invalid calendar date: ${date}`);
  return new Date(`${date}T00:00:00.000Z`);
}

export function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  return format(addDays(fromCalendarDate(date), days, { in: inUtc }), "yyyy-MM-dd", { in: inUtc });
}

/** Month ends clamp: 2026-01-31 + 1 month is 2026-02-28. */
export function addCalendarMonths(date: CalendarDate, months: number): CalendarDate {
  return format(addMonths(fromCalendarDate(date), months, { in: inUtc }), "yyyy-MM-dd", {
    in: inUtc,
  });
}

export function todayCalendarDate(now: Date = new Date()): CalendarDate {
  return toCalendarDate(now);
}

// ---------------------------------------------------------------------------
// Business days, always in Paris

export function startOfAppDay(value: DateInput): Date {
  return plain(startOfDay(requireDate(value), { in: inAppZone }));
}

export function endOfAppDay(value: DateInput): Date {
  return plain(endOfDay(requireDate(value), { in: inAppZone }));
}

export function startOfAppMonth(value: DateInput): Date {
  return plain(startOfMonth(requireDate(value), { in: inAppZone }));
}

export function endOfAppMonth(value: DateInput): Date {
  return plain(endOfMonth(requireDate(value), { in: inAppZone }));
}

export function startOfAppYear(value: DateInput): Date {
  return plain(startOfYear(requireDate(value), { in: inAppZone }));
}

export function endOfAppYear(value: DateInput): Date {
  return plain(endOfYear(requireDate(value), { in: inAppZone }));
}

/** Same wall-clock time `days` later in Paris (DST-safe). */
export function addAppDays(value: DateInput, days: number): Date {
  return plain(addDays(requireDate(value), days, { in: inAppZone }));
}

export function isSameAppDay(left: DateInput, right: DateInput): boolean {
  return isSameDay(requireDate(left), requireDate(right), { in: inAppZone });
}

/** Paris calendar days from `from` to `to` (positive when `to` is later). */
export function appCalendarDaysBetween(from: DateInput, to: DateInput): number {
  return differenceInCalendarDays(requireDate(to), requireDate(from), { in: inAppZone });
}

// ---------------------------------------------------------------------------
// Display, always in Paris

/** Formats with a custom date-fns pattern; prefer the named formatters below. */
export function formatAppDate(
  value: DateInput | null | undefined,
  pattern: string,
  { locale = "fr", fallback = "—" }: FormatDateOptions = {}
): string {
  const date = toDate(value);
  if (!date) return fallback;
  return format(date, pattern, { in: inAppZone, locale: locales[locale] });
}

/** 25/10/2026. A `YYYY-MM-DD` value parses as UTC midnight, which is that same day in Paris. */
export function formatDate(value: DateInput | null | undefined, options?: FormatDateOptions) {
  return formatAppDate(value, DATE_PATTERNS.date, options);
}

/** 25/10/2026 14:28 */
export function formatDateTime(value: DateInput | null | undefined, options?: FormatDateOptions) {
  return formatAppDate(value, DATE_PATTERNS.dateTime, options);
}

/** 14:28 */
export function formatTime(value: DateInput | null | undefined, options?: FormatDateOptions) {
  return formatAppDate(value, DATE_PATTERNS.time, options);
}

/** dimanche 25 octobre 2026 */
export function formatLongDate(value: DateInput | null | undefined, options?: FormatDateOptions) {
  return formatAppDate(value, DATE_PATTERNS.longDate, options);
}

/** dimanche 25 octobre 2026 à 14:28 */
export function formatLongDateTime(
  value: DateInput | null | undefined,
  options: FormatDateOptions = {}
) {
  return formatAppDate(value, DATE_PATTERNS.longDateTime[options.locale ?? "fr"], options);
}

/** il y a 3 jours / dans 2 heures */
export function formatRelative(
  value: DateInput | null | undefined,
  { now = new Date(), ...options }: FormatDateOptions & { now?: Date } = {}
): string {
  const date = toDate(value);
  if (!date) return options.fallback ?? "—";
  return formatDistance(date, now, { addSuffix: true, locale: locales[options.locale ?? "fr"] });
}

// ---------------------------------------------------------------------------
// <input type="datetime-local">, read and written as Paris wall-clock time

export function toDateTimeLocalInput(value: DateInput | null | undefined): string {
  return formatAppDate(value, "yyyy-MM-dd'T'HH:mm", { fallback: "" });
}

export function fromDateTimeLocalInput(value: string): Date | null {
  const match = DATE_TIME_LOCAL.exec(value);
  if (!match) return null;
  const [, y, m, d, h, min, s = "0"] = match;
  const date = plain(new TZDate(+y, +m - 1, +d, +h, +min, +s, APP_TIME_ZONE));
  return isValid(date) ? date : null;
}

// ---------------------------------------------------------------------------

function requireDate(value: DateInput): Date {
  const date = toDate(value);
  if (!date) throw new RangeError(`Invalid date: ${String(value)}`);
  return date;
}

function plain(date: Date): Date {
  return new Date(date.getTime());
}
